<!-- eslint-disable vue/valid-v-slot -->
<template>
  <v-card flat>
    <v-card-title class="title font-weigth-light px-0 py-0 mb-4">
      Booking Report
    </v-card-title>
    <v-card-text class="px-0">
      <v-row>
        <v-col cols="3">
          <date-picker
            v-model="datePicker.start"
            :masks="datePicker.masks"
            :min-date="datePicker.minimum"
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                :value="inputValue"
                label="From Date"
                v-on="inputEvents"
                outlined
                clearable
                :rules="[
                  v => {
                    if (!datePicker.end) return true;
                    return !!v || 'Start Date Required';
                  }
                ]"
                hide-details
              />
            </template>
          </date-picker>
        </v-col>
        <v-col cols="3">
          <date-picker
            v-model="datePicker.end"
            :masks="datePicker.masks"
            :min-date="datePicker.start"
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                :value="inputValue"
                label="To Date"
                v-on="inputEvents"
                outlined
                clearable
                :rules="[
                  v => {
                    if (!datePicker.end) return true;
                    return !!v || 'End Date Required';
                  }
                ]"
                hide-details
              />
            </template>
          </date-picker>
        </v-col>
        <v-col cols="3">
          <v-combobox
            v-model="selectedAssetGroups"
            :items="assetGroups"
            item-text="name"
            item-value="id"
            label="Asset Group"
            multiple
            outlined
            required
            @change="changeAssetGroup; checkAllAssetGroup(false)"
          >
            <template #prepend-item>
              <v-list-item
                ripple
                @click="checkAllAssetGroup(true)"
              >
                <v-list-item-action class="mr-3">
                  <v-icon>{{ iconCheckAllAssetGroup }}</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ textCheckAllAssetGroup }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2" />
            </template>
            <template #selection="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.name }}</span>
              </v-chip>
              <span
                v-if="index === 1 && selectedAssetGroups.length > 1"
                class="grey--text caption"
              >
                (+{{ selectedAssetGroups.length - 1 }} Others)
              </span>
            </template>
          </v-combobox>
        </v-col>
        <v-col cols="3">
          <v-combobox
            v-model="selectedVehicle"
            :items="filteredVehicles"
            item-text="device_name"
            item-value="id"
            label="Vehicle"
            multiple
            outlined
            required
            @change="(items) => { changeVehicle(items); checkAllVehicle(false) }"
          >
            <template #prepend-item>
              <v-list-item
                ripple
                @click="checkAllVehicle(true)"
              >
                <v-list-item-action class="mr-3">
                  <v-icon>{{ iconCheckAllVehicle }}</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ textCheckAllVehicle }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2" />
            </template>
            <template #selection="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.device_name }}</span>
              </v-chip>
              <span
                v-if="index === 1 && selectedVehicle.length > 1"
                class="grey--text caption"
              >
                (+{{ selectedVehicle.length - 1 }} Others)&nbsp;
              </span>
            </template>
          </v-combobox>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          class="py-0 d-flex justify-end"
        >
          <v-btn
            border
            color="success"
            @click="downloadReport"
            class="mr-2"
            :disabled="downloadReportText === 'Downloading'"
          >
            {{ downloadReportText }}
          </v-btn>
          <v-btn
            border
            color="primary"
            @click="generateReport"
            :disabled="!selectedVehicle.length || !datePicker.start || !datePicker.end || isLoading"
          >
            {{ isLoading ? 'Generating' : 'Generate' }}
          </v-btn>
        </v-col>
      </v-row>
      <v-row v-if="isGenerate && selectedTabForDisplay.length">
        <v-col cols="12">
          <v-tabs
            bg-color="primary"
            v-model="selectedTab"
          >
            <v-icon
              :title="isJoin ? 'Expand' : 'Collapse'"
              @click="handleJoin"
            >
              {{ isJoin ? 'mdi-arrow-expand' : 'mdi-arrow-collapse' }}
            </v-icon>
            <v-tab
              ref="tab"
              v-for="tab in selectedTabForDisplay"
              :key="tab.id"
              :value="tab.id"
              @click="generateReport(tab.id)"
            >
              {{ tab.device_name }}
            </v-tab>
          </v-tabs>
          <v-data-table
            :headers="headers"
            :items="dataPerTab"
            class="font-weight-light pa-5"
            :loading="isLoading"
            loading-text="Loading"
            :footer-props="footerProps"
            :options.sync="paginationOptions"
          >
            <template #[`item.book_detail.start_geofence_id`]="{ item }">
              {{ getOriginName(item.book_detail) }}
            </template>
            <template #[`item.book_detail.vehicle_name`]="{ item }">
              {{ getVehicleName(item.book_detail) }}
            </template>
            <template #[`item.book_detail.end_geofence_id`]="{ item }">
              {{ getDestinationName(item.book_detail) }}
            </template>
            <template #[`item.book_detail.start_datetime`]="{ item }">
              {{ startDateFormat(item.book_detail) }}
            </template>
            <template #[`item.book_detail.end_datetime`]="{ item }">
              {{ endDateFormat(item.book_detail) }}
            </template>
            <template #[`item.first_name`]="{ item }">
              {{ item.first_name }} {{ item.last_name }}
            </template>
            <template #[`item.duration`]="{ item }">
              {{ durationToHumanize(item) }}
            </template>
            <template #[`item.book_detail.metadata.work_order`]="{ item }">
              {{ getDataWorkOrderById(item) }}
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-row v-if="isLoading && selectedTabForDisplay.length === 0">
        <v-col cols="12">
          <p class="text-center">
            Loading
          </p>
        </v-col>
      </v-row>
      <v-row v-if="isGenerate && !isLoading && selectedTabForDisplay.length === 0">
        <v-col cols="12">
          <p class="text-center">
            No Data
          </p>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from "moment";
import isUndefined from "lodash/isUndefined";
import isNull from "lodash/isNull";
import get from "lodash/get";
import { getBookingDestinationText, parseBookingMetadata } from "@/plugins/destination";

export default {
  data() {
    return {
      assetGroups: [],
      vehicles: [],
      assetTypes: [],
      geofences: [],
      selectedVehicle: [],
      selectedAssetGroups: [],
      selectedVehicleForTabs: [],
      datePicker: {
        start: '',
        startUnix: '',
        paramStart: '',
        end: '',
        endUnix: '',
        paramEnd: '',
        masks: {
          input: 'DD MMMM YYYY'
        },
      },
      selectedTab: '',
      headers: [
        { text: "Licence Plate", value: "book_detail.vehicle_name" },
        { text: "Branch", value: "book_detail.start_geofence_id" },
        { text: "Start Date", value: "book_detail.start_datetime" },
        { text: "End Date", value: "book_detail.end_datetime" },
        { text: "Booker", value: "first_name" },
        { text: "Period Duration", value: "duration" },
        { text: "Period Duration Days", value: "durationDays" },
        { text: "Period Duration Hours", value: "durationHours" },
        { text: "Work Order", value: "book_detail.metadata.work_order" },
        { text: "Report Duration Days", value: "reportDurationDays" },
        { text: "Report Duration Hours", value: "reportDurationHours" },
      ],
      footerProps: {
        itemsPerPageOptions: [2, 3, 5, 10, 20, 50, 100, 200, 500]
      },
      paginationOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['book_detail.start_datetime'],
        sortDesc: []
      },
      data: [],
      isLoading: false,
      isGenerate: false,
      timeout: 0,
      textCheckAllAssetGroup: 'CHECK ALL ASSET GROUP',
      iconCheckAllAssetGroup: 'mdi-check-all',
      textCheckAllVehicle: 'CHECK ALL VEHICLE',
      iconCheckAllVehicle: 'mdi-check-all',
      isJoin: true,
      workOrders: [],
      downloadReportText: 'Download'
    };
  },
  props: {
    params: {
      type: Object,
      required: false,
      default: () => ({
        vehicles: [],
        assetGroups: [],
        assetTypes: [],
        geofences: [],
      }),
    },
  },
  created() {
    if (this.isWSP || this.isIntimap) this.getDataWorkOrder();
  },
  mounted() {
    this.vehicles = this.params.vehicles;
    this.assetTypes = this.params.assetTypes;
    this.assetGroups = this.params.assetGroups.filter((group) => {
      const assetType = this.params.assetTypes.find((type) => type.id === group.metadata.asset_type);
      return assetType && assetType.is_vehicle == '1';
    });
    this.geofences = this.params.geofences;
  },
  watch: {
    "datePicker.start"(value) {
      const startDate = moment(value).format("YYYY-MM-DD");
      this.datePicker.paramStart = startDate;
    },
    "datePicker.end"(value) {
      const endDate = moment(value).format("YYYY-MM-DD");
      this.datePicker.paramEnd = endDate;
    },
  },
  computed: {
    dataPerTab() {
      const vehicle = this.selectedTabForDisplay[this.selectedTab];
      if (!vehicle) return [];
      let dataFilter = this.data
      if (vehicle.id !== '__JOIN__') {
        dataFilter = this.data.filter((item) => item.book_detail.vehicle_id === vehicle.id);
      }
      return dataFilter
        .sort((a, b) => {
          const startA = moment(a.book_detail.start_datetime);
          const startB = moment(b.book_detail.start_datetime);
          return startA.isBefore(startB) ? -1 : 1;
        })
        .map((item) => {
            item.duration = moment(item.book_detail.end_datetime).diff(moment(item.book_detail.start_datetime), 'minutes');
            item.durationDays = moment(item.book_detail.end_datetime).diff(moment(item.book_detail.start_datetime), 'days');
            item.durationHours = item.durationDays > 0 ? item.duration - (item.durationDays * 24 * 60) : item.duration;
            item.durationHours = item.durationHours > 0 ? (item.durationHours / 60).toFixed(2) : 0;

            const theStart = moment(item.book_detail.start_datetime);
            const theEnd = moment(item.book_detail.end_datetime);
            const reportStartDate = moment(this.datePicker.paramStart + ' 00:00:00');
            const reportEndDate = moment(this.datePicker.paramEnd + ' 23:59:59');

            const rangeStart = theStart.isBefore(reportStartDate) ? reportStartDate : theStart;
            const rangeEnd = theEnd.isAfter(reportEndDate) ? reportEndDate : theEnd;

            item.reportDurationDays = rangeEnd.diff(rangeStart, 'days');
            item.reportDuration = rangeEnd.diff(rangeStart, 'minutes');
            item.reportDurationHours = item.reportDurationDays > 0 ? item.reportDuration - (item.reportDurationDays * 24 * 60) : item.reportDuration;
            item.reportDurationHours = item.reportDurationHours > 0 ? (item.reportDurationHours / 60).toFixed(2) : 0;

            return item;
        });
    },
    filteredVehicles() {
      if (this.selectedAssetGroups.length) {
        return this.vehicles.filter(
          (vehicle) => this.selectedAssetGroups.some(
            (group) => {
              return group.members && group.members.find((member) => member.detail.id === vehicle.id);
            }
          )
        );
      }
      return this.vehicles;
    },
    selectedTabForDisplay() {
      if (this.isJoin) {
        return [{
          id: '__JOIN__',
          device_name: 'All'
        }]
      }
      return this.selectedVehicleForTabs;
    },
    isWSP() {
      return this.$store.state.clientid == 'H1O5G7P0N3JA';
    },
    isIntimap() {
      return this.$store.state.clientid == 'DSSTWWLLIHH7';
    },
  },
  methods: {
    getDataWorkOrder() {
      this.$store
        .dispatch("API_CALL", {
          $method: "get_work_order",
          $postData: {
            clientid: this.$store.state.clientid
          }
        })
        .then(response => {
          this.workOrders = response.data.data;
        });
    },
    handleJoin() {
      this.isJoin = !this.isJoin;
      this.selectedTab = 0;
      this.$nextTick(() => {
        this.$refs.tab[0].$el.click();
      });
    },
    checkAllAssetGroup(force = false) {
      if (force) {
        if (this.textCheckAllAssetGroup === 'CHECK ALL ASSET GROUP') {
          this.selectedAssetGroups = this.assetGroups;
          this.textCheckAllAssetGroup = 'UNCHECK ALL ASSET GROUP';
          this.iconCheckAllAssetGroup = 'mdi-close';
        } else {
          this.selectedAssetGroups = [];
          this.textCheckAllAssetGroup = 'CHECK ALL ASSET GROUP';
          this.iconCheckAllAssetGroup = 'mdi-check-all';
        }
      } else {
        if (this.selectedAssetGroups.length === this.assetGroups.length) {
          this.textCheckAllAssetGroup = 'UNCHECK ALL ASSET GROUP';
          this.iconCheckAllAssetGroup = 'mdi-close';
        } else {
          this.textCheckAllAssetGroup = 'CHECK ALL ASSET GROUP';
          this.iconCheckAllAssetGroup = 'mdi-check-all';
        }
      }
    },
    changeAssetGroup() {
      this.selectedAssetGroups = this.selectedAssetGroups.filter((assetGroup) => typeof assetGroup === 'object' && assetGroup.id);
    },
    checkAllVehicle(force = false) {
      if (force) {
        if (this.textCheckAllVehicle === 'CHECK ALL VEHICLE') {
          this.selectedVehicle = this.filteredVehicles;
          this.textCheckAllVehicle = 'UNCHECK ALL VEHICLE';
          this.iconCheckAllVehicle = 'mdi-close';
        } else {
          this.selectedVehicle = [];
          this.textCheckAllVehicle = 'CHECK ALL VEHICLE';
          this.iconCheckAllVehicle = 'mdi-check-all';
        }
      } else {
        if (this.selectedVehicle.length === this.filteredVehicles.length) {
          this.textCheckAllVehicle = 'UNCHECK ALL VEHICLE';
          this.iconCheckAllVehicle = 'mdi-close';
        } else {
          this.textCheckAllVehicle = 'CHECK ALL VEHICLE';
          this.iconCheckAllVehicle = 'mdi-check-all';
        }
      }
    },
    changeVehicle(items) {
      this.selectedVehicle = items.filter((vehicle) => typeof vehicle === 'object' && vehicle.id);
    },
    generateReport(vehicleId) {
      if (!this.selectedVehicle.length || !this.datePicker.start || !this.datePicker.end) return;
      if (this.selectedVehicle.length && this.datePicker.start && this.datePicker.end) {
        let selectedVehicleId = null;
        if (typeof vehicleId !== 'object') {
          selectedVehicleId = vehicleId;
        }
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          this.isLoading = true;
          const promise = selectedVehicleId == null ? this.getDataBookTabs() : Promise.resolve();
          promise.then(() => {
            if (selectedVehicleId == null) {
              if (this.selectedTabForDisplay.length) {
                this.selectedTab = 0;
                this.getDataBook(this.selectedTabForDisplay[0].id);
              } else {
                this.data = [];
                this.isGenerate = true;
                this.isLoading = false;
              }
            } else {
              this.getDataBook(selectedVehicleId);
            }
          })
        }, 500);
      }
    },
    downloadReport() {
      if (!this.selectedVehicle.length || !this.datePicker.start || !this.datePicker.end || this.downloadReportText == 'Downloading') return;
      if (this.selectedVehicle.length && this.datePicker.start && this.datePicker.end) {
        const vehicle = this.selectedTabForDisplay[this.selectedTab];
        this.downloadReportText = 'Downloading';
        const payload = {
          export: 1,
          report: 'booking',
          clientid: this.$store.state.clientid,
          start_date: this.datePicker.paramStart,
          end_date: this.datePicker.paramEnd,
          get_schedules: 1,
          get_recurring: 0,
        };
        if (vehicle && vehicle.id == '__JOIN__') {
          payload.is_join = 1;
        } else if (!vehicle) {
          payload.is_join = 1;
        }
        payload.vehicle_id = this.selectedVehicle.map((vehicle) => vehicle.id);
        if (this.isIntimap || this.isWSP) {
          payload.use_timezone_depot = 1;
        }
        this.$store.dispatch('API_CALL', {
          $method: 'get_all_book',
          $postData: payload,
        })
        .then((response) => {
          if (response.data.data.hasOwnProperty('file')) {
            const lastFileName = response.data.data.file.split('/').pop();
            const url = `${process.env.VUE_APP_CARPOOLING_URL}/../assets/uploads/carpooling/${lastFileName}`;
            fetch(url)
              .then(response => {
                this.downloadReportText = 'Download';
                if (!response.ok) {
                  throw new Error('Network response was not ok');
                }
                return response.blob();
              })
              .then(blob => {
                const downloadUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = downloadUrl;
                a.download = lastFileName;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(downloadUrl);
              })
              .catch(error => {
                console.error('There has been a problem with your fetch operation:', error);
              });
          }
        });
      }
    },
    getDataBookTabs() {
      return this.$store.dispatch('API_CALL', {
        $method: 'get_book_for_tabs',
        $postData: {
          vehicle_id: this.selectedVehicle.map((vehicle) => vehicle.id),
          clientid: this.$store.state.clientid,
          start_date: this.datePicker.paramStart,
          end_date: this.datePicker.paramEnd,
          get_schedules: 1,
          get_recurring: 0,
        },
      })
      .then((response) => {
        this.selectedVehicleForTabs = response.data.data.map((vehicle) => this.selectedVehicle.find((v) => v.id === vehicle.vehicle_id));
      });
    },
    getDataBook(vehicleId) {
      const postData = {
        clientid: this.$store.state.clientid,
        start_date: this.datePicker.paramStart,
        end_date: this.datePicker.paramEnd,
        get_schedules: 1,
        get_recurring: 0,
      }
      if (vehicleId != '__JOIN__') {
         postData.vehicle_id = vehicleId; 
      } else {
        postData.vehicle_id = this.selectedVehicle.map((vehicle) => vehicle.id);
      }
      this.isLoading = true;
      if (this.isIntimap || this.isWSP) {
        postData.use_timezone_depot = 1;
      }
      return this.$store.dispatch('API_CALL', {
        $method: 'get_all_book',
        $postData: postData,
      })
      .then((response) => {
        this.data = response.data.data.map(item => {
          if (!item.book_detail.vehicle_name) {
            item.book_detail.vehicle_name = this.vehicles.find((vehicle) => vehicle.id === item.book_detail.vehicle_id).device_name;
          }
          try {
            item.book_detail.metadata = JSON.parse(item.book_detail.metadata);
          } catch (e) {}
          return item;
        });
        this.isGenerate = true;
        this.isLoading = false;
      });
    },
    getOriginName(item) {
      let destination = '';
      if (item.start_geofence_id) {
        this.geofences[this.$store.state.clientid]
          .forEach((group) => {
            const geofenceDestination = group.geofences.find(
              (geofence) =>
                parseInt(geofence.Id) === parseInt(item.start_geofence_id)
            );
            if (!isUndefined(geofenceDestination)) {
              destination = geofenceDestination.name;
            }
          });
      } else {
        const metadata = parseBookingMetadata(item.metadata);
        destination = metadata && metadata.address ? metadata.address : '';
      }
      return destination;
    },
    getVehicleName(item) {
      if (item == null) return;
      let metadata = '';
      if (!isNull(item.vehicle_name)) {
        metadata = `Vehicle - ${item.vehicle_name}`;
      } else if (!isNull(item.asset_id)) {
        const typeName = this.assetTypes.find((d) => d.id == item.asset_type);
        metadata = `${get(typeName, 'name', '-')} - ${item.asset_number}`;
      } else {
        metadata = '--';
      }
      if (!metadata) return;
      return metadata;
    },
    getDestinationName(item) {
      let destination = '';
      if (item.end_geofence_id) {
        this.geofences[this.$store.state.clientid]
          .forEach((group) => {
            const geofenceDestination = group.geofences.find(
              (geofence) =>
                parseInt(geofence.Id) === parseInt(item.end_geofence_id)
            );
            if (!isUndefined(geofenceDestination)) {
              destination = geofenceDestination.name;
            }
          });
      } else {
        destination = getBookingDestinationText(item.metadata, 2);
      }
      return destination;
    },
    startDateFormat(item) {
      if (item == null) return;
      let date = null;
      if (item.hasOwnProperty("start_datetime")) {
        date = moment(item.start_datetime).format('ddd, D MMM YYYY HH:mm')
      }
      return date;
    },
    endDateFormat(item) {
      if (item == null) return;
      let date = null;
      if (item.hasOwnProperty("end_datetime")) {
        date = moment(item.end_datetime).format('ddd, D MMM YYYY HH:mm')
      }
      return date;
    },
    durationToHumanize(item) {
        if (item.duration == null) return;

        const totalMinutes = item.duration;
        const days = Math.floor(totalMinutes / (24 * 60));
        const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
        const minutes = totalMinutes % 60;

        const dayPart = days > 0 ? `${days}d ` : '';
        const hourPart = hours > 0 ? `${hours}h` : '0h';
        const minutePart = minutes > 0 ? `${minutes}m` : '00m';

        return `${dayPart}${hourPart}:${minutePart}`;
    },
    getDataWorkOrderById(item) {
      if (item == null) return;
      let workOrder = '';
      if ( item.book_detail.metadata.hasOwnProperty("work_order")) {
        const workOrderData = this.workOrders.find((wo) => wo.code == item.book_detail.metadata.work_order);
        if (workOrderData) {
          workOrder = workOrderData.code;
        } else {
          workOrder = item.book_detail.metadata.work_order;
        }
      }
      return workOrder;
    },
  },
};
</script>
